'use client';

// Боковые слои приложения (план NATIVE-SLIDE): родной выезд справа в ЕДИНОМ WebView, БЕЗ
// перезагрузки. Содержимым слоя владеет веб (маршрут ИЛИ плашка) — натив только фотографирует
// уходящий экран и двигает карточку. Здесь — хендшейк с нативным мостом и стопка «как вернуться».
//
// Порядок выезда: layerPush (натив фотографирует текущий экран и убирает живой webHost за кадр
// справа) → веб МОЛЧА меняет содержимое (webHost за кадром — подмена не видна) → layerCommit
// (натив вдвигает карточку). Возврат: layerPopBegin (снимок в центр, живой за кадр) → веб делает
// шаг назад → layerPopCommit (натив убирает снимок). Свайп от края: натив шлёт событие
// layerPopped → веб делает шаг назад тем же путём.
//
// В браузере и на СТАРЫХ сборках приложения (нет layerPush) — примитив прозрачно вырождается в
// обычную навигацию: слоёв нет, просто navigate()/back(). Точка ветвления — hasNativeLayers().

import { useCallback } from 'react';
import { useRouter } from '@/i18n/routing';
import { log as journalLog } from './client-journal';
import { nativeBridge, nativeMethod } from './native-app';
import { tellActiveTab } from './open-dialog';

type BackFn = () => void | Promise<void>;

// Стопка «как вернуться»: по колбэку на каждый выехавший слой + его тег (порядковый номер).
// Глубину считает веб (натив складывает по снимку) — стопки идут в ногу, пока push/pop парные.
// Тег нужен хосту плашек: он показывает свою плашку ТОЛЬКО если она — вершина всего нативного
// стека (иначе сверху маршрут-слой, напр. сценарий, и плашку надо прятать). Маршруты и плашки
// живут в ОДНОМ стеке, поэтому нужен общий порядок, а не грубое скрытие по .cab-focus.
let layerSeq = 0;
const backStack: { tag: number; back: BackFn }[] = [];
const topListeners = new Set<() => void>();
// «Слои полностью улеглись»: стек пуст И натив ДОКОНЧИЛ снятие карточки (layerPopCommit).
// Нужно оболочке: configureTabBar на нативе перевешивает WebView во вкладку без оглядки на
// слой — звать его можно только когда контейнер слоёв уже разобран (смена языка 2026-07-23).
const idleListeners = new Set<() => void>();
let poppedBound = false;
let pushing = 0; // сколько выездов слоя прямо сейчас в полёте

/** Слой открыт ИЛИ прямо сейчас выезжает. Оболочка по нему НЕ шлёт нативу setActiveTab: смена
 *  раздела при открытии сценария (overview→automations) иначе выдёргивает webHost из контейнера
 *  слоя во вкладку и выезд застывает (владелец 2026-07-21). Натив это тоже стережёт, но веб-гвард
 *  чинит и уже установленные сборки. */
export function nativeLayerActive(): boolean {
  return pushing > 0 || backStack.length > 0;
}

function notifyTop(): void {
  topListeners.forEach((f) => f());
}

/** Следующий уникальный тег слоя (общий счётчик для маршрутов и плашек). */
export function nextLayerTag(): number {
  return ++layerSeq;
}

/** Слой с таким тегом ещё в стопке? (0 — «нет слоёв» — считается живым всегда.) */
export function hasLayerTag(tag: number): boolean {
  return tag === 0 || backStack.some((l) => l.tag === tag);
}

/** Тег вершины нативного стека (0 — пусто). Хост плашек сравнивает с тегом своей верхней. */
export function topLayerTag(): number {
  return backStack.length ? backStack[backStack.length - 1]!.tag : 0;
}

/** Подписка на смену вершины стека (push/pop любого слоя) — хост перерисовывает видимость. */
export function subscribeTopLayer(fn: () => void): () => void {
  topListeners.add(fn);
  return () => {
    topListeners.delete(fn);
  };
}

/** Подписка «слои полностью улеглись»: стек пуст и нативная карточка ДОСНЯТА (после
 *  layerPopCommit), webHost уже вернулся во вкладку. Ровно тут можно звать configureTabBar —
 *  раньше натив выдернул бы WebView из живого контейнера слоя (вебвью замирает). */
export function onLayersIdle(fn: () => void): () => void {
  idleListeners.add(fn);
  return () => {
    idleListeners.delete(fn);
  };
}

function notifyIdleIfSettled(): void {
  if (!nativeLayerActive()) idleListeners.forEach((f) => f());
}

/** Два кадра: даём вебу отрисовать новое содержимое, прежде чем натив вдвинет/уберёт карточку. */
function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'undefined') {
      resolve();
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/** Дождаться, пока новое содержимое РЕАЛЬНО нарисовано, прежде чем натив вдвинет карточку —
 *  иначе в неё попадает старый экран (баг «сначала Сценарии, потом контент», владелец
 *  2026-07-20). ready() ждёт признак готовности (напр. появление открытого сценария .cab-focus);
 *  по умолчанию — два кадра. Кап по времени, чтобы не подвиснуть на медленной странице. */
function waitForSelector(selector: string, capMs = 1200): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'undefined' || typeof performance === 'undefined') {
      resolve();
      return;
    }
    const start = performance.now();
    const tick = () => {
      if (document.querySelector(selector) || performance.now() - start > capMs) {
        requestAnimationFrame(() => resolve()); // ещё кадр — чтобы попало в отрисовку
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/** Готовность открытого экрана сценария (фокус-режим применён — список раздела уже скрыт,
 *  видно сам сценарий/его скелетон, а не старый список). Кап 3500: на проде (мобильная сеть)
 *  роут нового черновика грузится дольше 1200 — карточка въезжала со СПИСКОМ внутри
 *  (владелец 2026-07-21, видео «из шаблона»). */
export function scenarioReady(): Promise<void> {
  return waitForSelector('.cab.cab-focus', 3500);
}

/** Готовность СТРАНИЦЫ ОДНОГО БОТА (/app/bots/<id>/stats): она живёт внутри раздела
 *  «Боты», а не фокус-режимом, — .cab-focus у неё не появляется никогда, и ждать его
 *  значило бы держать въезд карточки все 3.5 секунды капа. Ждём саму страницу. */
export function botStatsReady(): Promise<void> {
  return waitForSelector('.bot-stats-head', 3500);
}

/** Приложение умеет боковые слои (новая сборка с мостом layer*). */
export function hasNativeLayers(): boolean {
  // НЕ по наличию свойства: мост — Proxy, свойство есть всегда (капкан nativeMethod).
  return nativeMethod('layerPush');
}

/** Открыт хотя бы один боковой слой (есть что снимать через popLayer). Экраны, которые
 *  умеют быть и слоем, и обычной страницей (сценарий), по нему решают: закрываться выездом
 *  назад (popLayer) или обычной навигацией. */
export function nativeLayerOpen(): boolean {
  return backStack.length > 0;
}

/** Подписка на нативный свайп-назад (один раз): натив сообщает «человек смахнул слой» —
 *  веб делает шаг назад тем же колбэком и подтверждает нативу (layerPopCommit). */
async function bindPopped(): Promise<void> {
  if (poppedBound) return;
  const br = nativeBridge();
  if (!br?.addListener) return;
  poppedBound = true;
  await br.addListener('layerPopped', () => {
    void runBack();
  });
}

/** Снять верхний слой: шаг назад в вебе + подтверждение нативу. Общий хвост свайпа и popLayer. */
async function runBack(): Promise<void> {
  const top = backStack.pop();
  // Журнал клиента (25.08.2026): выезды и возвраты слоёв — половина разбора зависаний в
  // приложении (reload под открытым слоем вешает его насмерть, §11).
  journalLog('layer.pop', { tag: top?.tag ?? 0, left: backStack.length });
  notifyTop();
  if (top) await top.back();
  await nextPaint();
  await nativeBridge()?.layerPopCommit?.();
  notifyIdleIfSettled();
}

/** Выехать боковым слоем. navigate — сменить содержимое (router.push / открыть плашку);
 *  back — как вернуться (router.back / закрыть плашку), зовётся при свайпе-назад или popLayer.
 *  Без нативных слоёв — просто navigate() (слоёв нет). */
export async function pushLayer(
  navigate: () => void | Promise<void>,
  back: BackFn,
  ready?: () => Promise<void>,
  tag?: number,
  bottom?: boolean,
  // Низкий нижний лист: доля экрана снизу (0..1), напр. 0.4 у подтверждений.
  // Старые сборки ключ игнорируют (высокий лист) — деградация честная.
  detent?: number,
  // СТОПКА (Н39, владелец 02.09.2026): тот же нижний лист, но экран под ним уходит назад
  // колодой и за ним встаёт фирменная подложка (§11 п.18) — вид КОНЕЧНОГО ОБЪЕКТА из
  // списка. Едет ОТДЕЛЬНЫМ методом моста (layerPushDeck): контракт заморожен, у layerPush
  // смысла не меняем. Сборка метода не знает — лист приезжает как раньше, одиночной
  // вертикальной шторкой.
  deck?: boolean,
): Promise<void> {
  const br = nativeBridge();
  if (!br || !nativeMethod('layerPush')) {
    await navigate();
    return;
  }
  pushing += 1;
  try {
    await bindPopped();
    // НАЖАТАЯ КНОПКА В СНИМКЕ (владелец 25.08.2026): слой открывается тапом по веб-кнопке,
    // её отжатие (`:active` → покой) анимировано переходом, а натив фотографирует экран под
    // слой в тот же миг — на снимке кнопка полунажатая, и при возврате слоя она «потухает»
    // уже на живом экране. Перед снимком на кадр выключаем переходы (html.snap-calm,
    // cabinet.css): элемент встаёт в покой мгновенно, затем снимок. Ожидание — два кадра
    // отрисовки, для человека это ноль. Класс снимаем после снимка — выезд самого слоя и
    // его содержимого анимируются как раньше.
    const calm = typeof document !== 'undefined' ? document.documentElement : null;
    calm?.classList.add('snap-calm');
    try {
      await nextPaint();
      const opts = bottom ? { bottom: true, ...(detent ? { detent } : {}) } : undefined;
      if (deck && bottom && nativeMethod('layerPushDeck')) await br.layerPushDeck!(opts);
      else await br.layerPush!(opts);
    } finally {
      calm?.classList.remove('snap-calm');
    }
    await navigate();
    // Ждём, пока новый экран нарисован (иначе карточка вдвинется со старым содержимым).
    await (ready ? ready() : nextPaint());
    const pushed = tag ?? ++layerSeq;
    backStack.push({ tag: pushed, back });
    journalLog('layer.push', { tag: pushed, depth: backStack.length, ...(bottom ? { bottom: 1 } : {}) });
    notifyTop();
    await br.layerCommit?.();
  } finally {
    pushing -= 1;
  }
}

/** Закрыть верхний слой самим вебом (× или кнопка «назад» в вебе). Без нативных слоёв —
 *  просто back() (снятого колбэка нет — вызывающий сам решает, как вернуться). */
export async function popLayer(): Promise<void> {
  const br = nativeBridge();
  if (!br || !nativeMethod('layerPopBegin')) {
    const top = backStack.pop();
    journalLog('layer.pop', { tag: top?.tag ?? 0, left: backStack.length, web: 1 });
    notifyTop();
    if (top) await top.back();
    notifyIdleIfSettled();
    return;
  }
  await br.layerPopBegin!();
  await runBack();
}

/** Снять ВСЕ открытые слои (закрыть целый флоу целиком, каскад родных выездов назад).
 *  Для «результатных» переходов: создание сценария из шаблона/импорта/меню закрывает всю
 *  стопку создания и открывает сценарий ОДНИМ слоем над корневым экраном (владелец
 *  2026-07-21: «все предыдущие шаги должны убраться»). Веб/нет слоёв — мгновенный noop. */
export async function popAllLayers(): Promise<void> {
  let guard = 10; // защитный кап — не крутиться вечно, если стек разошёлся
  while (backStack.length > 0 && guard-- > 0) await popLayer();
}

/** МОЛЧА схлопнуть слои ПОД верхним: остаётся [корень] + верхняя карточка, «назад» с неё
 *  ведёт сразу на корень (владелец 2026-07-21: «закрыл — оказался в списке», без каскада
 *  анимаций). Требование: промежуточные слои — ПЛАШКИ (их back() чистит только веб-состояние
 *  хоста, без нативных команд и навигации); маршрутные слои так снимать нельзя (back() —
 *  history.back, уехала бы навигация). Натив убирает свои невидимые снимки одним layerFlatten.
 *  Возвращает false на старой сборке без layerFlatten — вызывающий берёт popAllLayers-каскад. */
export async function flattenUnderTop(): Promise<boolean> {
  const br = nativeBridge();
  if (!br || !nativeMethod('layerFlatten')) return false;
  if (backStack.length > 1) {
    const top = backStack.pop()!;
    while (backStack.length > 0) {
      const mid = backStack.pop()!;
      await mid.back(); // плашка: снять из host-стека (чистый веб, ничего не анимирует)
    }
    backStack.push(top);
    notifyTop();
  }
  await br.layerFlatten!();
  return true;
}

/** «Результатный» переход на созданный сценарий (вынесено из useCreateGo — тем же путём
 *  идёт страница «Вручную»): визуально ОДИН выезд справа поверх текущего экрана; слои
 *  создания под ним снимаются МОЛЧА (layerFlatten), «назад» со сценария ведёт сразу на
 *  корневой экран. Старая сборка без layerFlatten — каскад выездов назад, затем выезд.
 *  Веб/нет слоёв — просто nav(). */
export function openScenarioResult(nav: () => void, ready?: () => Promise<void>): void {
  if (!hasNativeLayers()) {
    nav();
    return;
  }
  // Экран другой формы присылает СВОЙ признак готовности: страница цифр (созданная
  // рассылка уходит на неё) живёт внутри раздела, .cab-focus у неё не бывает — без этого
  // выезд ждал бы весь кап 3,5 с и всё это время показывал старый экран.
  const wait = ready ?? scenarioReady;
  void (async () => {
    const back = () => window.history.back();
    if (nativeMethod('layerFlatten')) { // НЕ по свойству: мост — Proxy (капкан nativeMethod)
      await pushLayer(nav, back, wait); // ждём открытый редактор, не старый список
      await flattenUnderTop();
      return;
    }
    await popAllLayers();
    await pushLayer(nav, back, wait);
  })();
}

/** ВОЗВРАТ К СПИСКУ БОТОВ ПОСЛЕ СОЗДАНИЯ (владелец 2026-08-07: «после создания веди на
 *  все боты, а не в открытый сценарий»). В приложении флоу создания закрывается ЦЕЛИКОМ:
 *  все его слои снимаются родными выездами назад до корневого экрана, и нового слоя НЕ
 *  открываем вовсе — человек оказывается в списке, где новый бот подсвечен меткой
 *  «только что создан». Веб/старая сборка — просто nav() (слоёв нет).
 *
 *  INVARIANT(scriptera): здесь НЕТ pushLayer. Причина: одна нативная операция за раз —
 *  push результата в тот же тик, что закрытие поверхностей создания, заставлял натив
 *  фотографировать экран дважды и рисовать панель в двух экземплярах (видео владельца
 *  2026-08-06). Возврат к списку — только pop/flatten. Канон: docs/NATIVE-SLIDE-PLAN.md
 *  §11 п.11 («каскад закрытий запрещён» — поэтому и снимаем стопку ОДНИМ путём, а не
 *  вперемешку с onDone поверхностей). */
export function closeCreateToList(nav: () => void): void {
  if (!hasNativeLayers()) {
    nav();
    return;
  }
  void (async () => {
    await popAllLayers();
    nav();
    announceBotsTab();
  })();
}

/**
 * ПОСЛЕ СОЗДАНИЯ БОТА — ВКЛАДКА «БОТЫ» РОДНОГО МЕНЮ (владелец 18.09.2026: «когда мы
 * создаём бота, ты переходил обязательно на папку боты»). Оболочка (shell.tsx) вяжет
 * вкладку к разделу адреса, но пока открыт слой — не трогает её, а после закрытия
 * слоёв заново не спрашивает: бот, созданный из «Чатов», уводил веб на список ботов,
 * а меню оставалось на «Чатах». Тут вкладка объявляется явно после снятия слоёв;
 * в браузере родного меню нет — тихо.
 */
export function announceBotsTab(): void {
  const said = nativeBridge()?.setActiveTab?.({ key: 'bots' });
  if (said) void said.then(() => tellActiveTab('bots')).catch(() => tellActiveTab('bots'));
}

/** Хук-открывашка маршрута боковым выездом. Возвращает функцию: если приложение умеет слои —
 *  открывает href родным выездом и возвращает true (вызывающий гасит обычную навигацию Link);
 *  иначе false — пусть отработает обычный переход. Возврат — нативным свайпом или popLayer(). */
export function useSlideOpen(): (href: string, ready?: () => Promise<void>) => boolean {
  const router = useRouter();
  return useCallback(
    (href: string, ready?: () => Promise<void>): boolean => {
      if (!hasNativeLayers()) return false;
      void pushLayer(
        () => router.push(href),
        () => window.history.back(),
        // По умолчанию ждём открытый сценарий, не старый список. Экран другой формы
        // (страница бота живёт в разделе, без .cab-focus) присылает свой признак
        // готовности — механика выезда при этом ОДНА, второй не заводим.
        ready ?? scenarioReady,
      );
      return true;
    },
    [router],
  );
}
